import type { OwnerPayload, WorldItemStatus, WorldSettings, WorldShell } from '../../shared/coloring/world/types';
import { ALL_SHAPE_IDS, FREEFORM_SHAPE_ID, SHAPE_LABELS, type MeadowShapeId, type WingShapeId } from '../butterfly-meadow/layout';
import { BUTTERFLY_SPECIES, type ButterflySpecies } from '../butterfly-meadow/species';
import { readyArtDataUrl } from '../butterfly-meadow/ready-art';

export type ButterflyWork = {
  id: string;
  shape: MeadowShapeId;
  source: 'upload' | 'preset';
  imageUrl: string;
  createdAt: string;
  status: WorldItemStatus;
  /** Fixed scale for a ready-made species; guest artwork keeps the runtime's natural scale. */
  size?: number;
  /** Только в ответе мастер-админки. */
  sizeBytes?: number;
};

export type ButterflySettings = WorldSettings & { shapes?: WingShapeId[] };
export type ButterflyWorld = WorldShell<ButterflySettings> & { works: ButterflyWork[] };
export type ButterflyOwnerPayload = OwnerPayload<ButterflyWorld>;

const seg = encodeURIComponent;
export const butterflyPaths = {
  manage: '/butterflies/manage',
  display: (code: string) => `/butterflies/display/${seg(code)}`,
  add: (code: string) => `/butterflies/add/${seg(code)}`,
  draw: (code: string) => `/butterflies/draw/${seg(code)}`,
  capture: (code: string) => `/butterflies/capture/${seg(code)}`,
  print: (code: string) => `/butterflies/print/${seg(code)}`,
  moderator: (secret: string) => `/butterflies/moderator/${seg(secret)}`,
};

export const shapeLabel = (shape: MeadowShapeId) => SHAPE_LABELS[shape];

/**
 * Формы, доступные миру: печатные всем, «Своя бабочка» — только платному миру (сервер отдаёт тот же список в
 * `settings.shapes`, а при загрузке в пробном мире отвечает 403).
 */
export function worldShapes(world: Pick<ButterflyWorld, 'settings'>): WingShapeId[] {
  const offered: readonly WingShapeId[] = world.settings.shapes?.length ? world.settings.shapes : ALL_SHAPE_IDS;
  return offered.filter((shape) => shape !== FREEFORM_SHAPE_ID || world.settings.plan !== 'trial');
}

/** 16 готовых видов, выпускаемых прямо на луг. */
export const BUTTERFLY_PRESETS: readonly ButterflySpecies[] = BUTTERFLY_SPECIES;

export function butterflyPresetImage(species: ButterflySpecies): string {
  return readyArtDataUrl(species);
}

/** Старые пресеты и пользовательские рисунки хранят URL; новые виды восстанавливают арт по id формы. */
export function butterflyWorkImage(work: Pick<ButterflyWork, 'shape' | 'source' | 'imageUrl'>): string {
  if (work.source === 'preset') {
    const species = BUTTERFLY_SPECIES.find((item) => item.shape === work.shape);
    if (species) return readyArtDataUrl(species);
  }
  return work.imageUrl;
}

export type AddingBlock = 'expired' | 'disabled' | 'full';

/** Почему гость не может отправить бабочку; `null` — можно. */
export function butterflyAddingBlock(world: Pick<ButterflyWorld, 'status' | 'settings'>): AddingBlock | null {
  if (world.status !== 'active') return 'expired';
  if (!world.settings.guestUploadEnabled) return 'disabled';
  if (!world.settings.canAddItem) return 'full';
  return null;
}

export function addingBlockText(block: AddingBlock): string {
  switch (block) {
    case 'expired': return 'Срок доступа к лугу истёк. Попросите владельца продлить его.';
    case 'disabled': return 'Владелец выключил добавление бабочек.';
    default: return 'На лугу больше нет места. В пробной версии можно добавить только несколько бабочек.';
  }
}
